import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';
import starlineLogo from '@/assets/starline-logo.webp';
import { useLanguage } from '@/contexts/LanguageContext';
import { FacebookIcon, InstagramIcon, LinkedinIcon, WhatsappIcon } from '@/components/BrandIcons';

/**
 * Social profiles.
 *
 * `hoverClass` carries each platform's own brand colour, applied only on hover
 * so the row reads as one calm set at rest and comes alive under the cursor —
 * four saturated logos sitting permanently in a footer looks like a sticker
 * sheet, not a premium brand.
 *
 * NOTE: the Facebook page and the WhatsApp number are Star Line's real ones,
 * taken from their own printed artwork. Instagram and LinkedIn are assumed
 * handles and must be confirmed before launch.
 */
const socials: {
  key: string;
  label: string;
  href: string;
  Icon: (props: { className?: string }) => JSX.Element;
  hoverClass: string;
}[] = [
  {
    key: 'facebook',
    label: 'Facebook',
    href: 'https://www.facebook.com/starlinespecialbusservice',
    Icon: FacebookIcon,
    hoverClass: 'hover:text-[#1877F2] hover:border-[#1877F2]/40 hover:shadow-[0_0_20px_-4px_#1877F2]',
  },
  {
    key: 'instagram',
    label: 'Instagram',
    href: 'https://www.instagram.com/starlinespecialbusservice',
    Icon: InstagramIcon,
    hoverClass: 'hover:text-[#E1306C] hover:border-[#E1306C]/40 hover:shadow-[0_0_20px_-4px_#E1306C]',
  },
  {
    key: 'linkedin',
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/starlinespecial',
    Icon: LinkedinIcon,
    hoverClass: 'hover:text-[#0A66C2] hover:border-[#0A66C2]/40 hover:shadow-[0_0_20px_-4px_#0A66C2]',
  },
  {
    key: 'whatsapp',
    label: 'WhatsApp',
    href: 'https://wa.me/8801973259904',
    Icon: WhatsappIcon,
    hoverClass: 'hover:text-[#25D366] hover:border-[#25D366]/40 hover:shadow-[0_0_20px_-4px_#25D366]',
  },
];

const quickLinks: [string, string][] = [
  ['/', 'nav_home'],
  ['/search', 'nav_search'],
  ['/routes', 'nav_routes'],
  ['/counters', 'nav_counters'],
  ['/notices', 'footer_travel_updates'],
  ['/manage-booking', 'nav_manage'],
];

const legalLinks: [string, string][] = [
  ['/support', 'footer_help_center'],
  ['/terms', 'footer_terms'],
  ['/privacy', 'footer_privacy'],
  ['/support', 'footer_contact_us'],
];

export default function Footer() {
  const { t } = useLanguage();

  return (
    <footer className="bg-card/60 border-t border-border/40">
      <div className="container py-14">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div>
            <div className="mb-5">
              <img src={starlineLogo} alt="Star Line Group" className="h-12 w-auto" loading="lazy" width={48} height={48} />
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t('footer_tagline')}
            </p>

            <div className="mt-7">
              <h4 className="font-display font-semibold text-sm mb-4 tracking-wide uppercase text-foreground/80">
                {t('footer_follow_us')}
              </h4>
              <div className="flex items-center gap-2.5">
                {socials.map(({ key, label, href, Icon, hoverClass }) => (
                  <a
                    key={key}
                    href={href}
                    target="_blank"
                    // noopener/noreferrer: without them the opened page gets a
                    // handle on this window through window.opener.
                    rel="noopener noreferrer"
                    aria-label={`${label} — ${t('footer_follow_us')}`}
                    title={label}
                    className={`
                      w-10 h-10 rounded-xl flex items-center justify-center
                      bg-secondary/50 border border-border/40 text-muted-foreground
                      transition-all duration-300 hover:-translate-y-0.5
                      focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50
                      ${hoverClass}
                    `}
                  >
                    <Icon className="w-[18px] h-[18px]" />
                  </a>
                ))}
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-display font-semibold text-sm mb-4 tracking-wide uppercase text-foreground/80">{t('footer_quick_links')}</h4>
            <div className="flex flex-col gap-2.5">
              {quickLinks.map(([to, key], i) => (
                <Link key={i} to={to} className="text-sm text-muted-foreground hover:text-foreground transition-colors">{t(key)}</Link>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-display font-semibold text-sm mb-4 tracking-wide uppercase text-foreground/80">{t('footer_support_legal')}</h4>
            <div className="flex flex-col gap-2.5">
              {legalLinks.map(([to, key], i) => (
                <Link key={i} to={to} className="text-sm text-muted-foreground hover:text-foreground transition-colors">{t(key)}</Link>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-display font-semibold text-sm mb-4 tracking-wide uppercase text-foreground/80">{t('footer_contact')}</h4>
            <div className="flex flex-col gap-3.5 text-sm text-muted-foreground">
              <a href="tel:+8801973259609" className="flex items-center gap-2.5 hover:text-foreground transition-colors cursor-pointer"><Phone className="w-4 h-4 text-primary" /> +8801973-259609</a>
              <a href="mailto:info@starline.com.bd" className="flex items-center gap-2.5 hover:text-foreground transition-colors cursor-pointer"><Mail className="w-4 h-4 text-primary" /> info@starline.com.bd</a>
              <div className="flex items-start gap-2.5"><MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" /> {t('footer_address')}</div>
            </div>
          </div>
        </div>
        <div className="mt-10 pt-8 border-t border-border/30 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <p>© 2026 Star Line Group. {t('footer_rights')}</p>

          {/* Developer credit. Set in a script face and baseline-aligned with
              the label so it reads as a signed piece of work rather than
              another footer link. Kept lighter than the legal links so it
              never competes with them. */}
          <p className="flex items-baseline gap-2 order-last sm:order-none">
            <span className="text-muted-foreground/70">{t('footer_developed_by')}</span>
            <span className="font-signature text-lg sm:text-xl leading-none text-foreground/90 translate-y-[1px]">
              Kazi Salman
            </span>
          </p>

          <div className="flex items-center gap-4">
            <Link to="/terms" className="hover:text-foreground transition-colors">{t('footer_terms')}</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">{t('footer_privacy')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
